import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Modal, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const STEP = 0.25;

const clampScale = (value: number) =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

interface ImageLightboxProps {
  open: boolean;
  src: string;
  alt?: string;
  onClose: () => void;
}

/**
 * Full-screen viewer for a single image: shows it at natural size, centred,
 * with zoom in/out and drag-to-pan once zoomed past 1x.
 *
 * Use `useImageLightbox()` rather than wiring this up by hand.
 */
const ImageLightbox: React.FC<ImageLightboxProps> = ({
  open,
  src,
  alt = '',
  onClose,
}) => {
  const [scale, setScale] = useState(MIN_SCALE);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Drag state lives in a ref: panning fires on every mousemove, and going
  // through state there would re-render the image on each frame.
  const drag = useRef<{ active: boolean; x: number; y: number }>({
    active: false,
    x: 0,
    y: 0,
  });

  const reset = useCallback(() => {
    setScale(MIN_SCALE);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Reopening on a different image should never inherit the previous zoom.
  useEffect(() => {
    if (open) reset();
  }, [open, src, reset]);

  const zoomTo = useCallback((next: number) => {
    const clamped = clampScale(next);
    setScale(clamped);
    // Back at 1x the image fits the frame, so any pan offset would just
    // push it off-centre.
    if (clamped === MIN_SCALE) setOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === '+' || event.key === '=') zoomTo(scale + STEP);
      if (event.key === '-' || event.key === '_') zoomTo(scale - STEP);
      if (event.key === '0') reset();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, scale, zoomTo, reset]);

  const handleWheel = (event: React.WheelEvent) => {
    zoomTo(scale + (event.deltaY < 0 ? STEP : -STEP));
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (scale === MIN_SCALE) return;
    event.preventDefault();
    drag.current = {
      active: true,
      x: event.clientX - offset.x,
      y: event.clientY - offset.y,
    };
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!drag.current.active) return;
    setOffset({
      x: event.clientX - drag.current.x,
      y: event.clientY - drag.current.y,
    });
  };

  const endDrag = () => {
    drag.current.active = false;
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(16, 24, 40, 0.88)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          outline: 'none',
        }}
      >
        {/* Toolbar sits above the backdrop; its own onClick stops the
            backdrop handler from closing the modal on every button press. */}
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            display: 'flex',
            gap: 1,
            zIndex: 1,
            direction: 'ltr',
          }}
        >
          <Tooltip title="بزرگ‌نمایی">
            <span>
              <IconButton
                onClick={() => zoomTo(scale + STEP)}
                disabled={scale >= MAX_SCALE}
                sx={toolbarButtonSx}
              >
                <ZoomInIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="کوچک‌نمایی">
            <span>
              <IconButton
                onClick={() => zoomTo(scale - STEP)}
                disabled={scale <= MIN_SCALE}
                sx={toolbarButtonSx}
              >
                <ZoomOutIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="اندازه اصلی">
            <span>
              <IconButton
                onClick={reset}
                disabled={scale === MIN_SCALE}
                sx={toolbarButtonSx}
              >
                <RestartAltIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Tooltip title="بستن">
          <IconButton
            onClick={onClose}
            sx={{ ...toolbarButtonSx, position: 'absolute', top: 16, right: 16 }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>

        <Box
          component="img"
          src={src}
          alt={alt}
          draggable={false}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          sx={{
            maxWidth: '92vw',
            maxHeight: '88vh',
            objectFit: 'contain',
            userSelect: 'none',
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            // Skip the transition while dragging, or the image lags the cursor.
            transition: drag.current.active ? 'none' : 'transform .15s ease',
            cursor:
              scale > MIN_SCALE
                ? drag.current.active
                  ? 'grabbing'
                  : 'grab'
                : 'default',
          }}
        />
      </Box>
    </Modal>
  );
};

const toolbarButtonSx = {
  color: '#FFFFFF',
  backgroundColor: 'rgba(255, 255, 255, 0.12)',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.24)' },
  '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.35)' },
} as const;

/**
 * Wires an image up to the lightbox:
 *
 *   const lightbox = useImageLightbox();
 *   <img src={url} {...lightbox.triggerProps(url, 'alt text')} />
 *   {lightbox.element}
 */
export const useImageLightbox = () => {
  const [state, setState] = useState<{ src: string; alt: string } | null>(null);

  const open = useCallback((src: string, alt = '') => {
    if (src) setState({ src, alt });
  }, []);

  const close = useCallback(() => setState(null), []);

  const triggerProps = useCallback(
    (src: string, alt = '') => ({
      onClick: (event: React.MouseEvent) => {
        event.stopPropagation();
        open(src, alt);
      },
      role: 'button' as const,
      tabIndex: 0,
      'aria-label': alt ? `${alt} — نمایش در اندازه اصلی` : 'نمایش تصویر',
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open(src, alt);
        }
      },
      style: { cursor: 'zoom-in' as const },
    }),
    [open],
  );

  return {
    open,
    close,
    triggerProps,
    element: (
      <ImageLightbox
        open={state !== null}
        src={state?.src ?? ''}
        alt={state?.alt}
        onClose={close}
      />
    ),
  };
};

export default ImageLightbox;
